import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  PlusIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { verificationApi } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { Layout } from '../components/shared/Layout';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { Alert } from '../components/shared/Alert';
import type { VerificationStatus } from '../types';

const statusConfig: Record<
  VerificationStatus,
  { label: string; color: string; icon: typeof ClockIcon }
> = {
  Draft: { label: 'Draft', color: 'text-gray-500 bg-gray-100', icon: DocumentTextIcon },
  PendingPayment: { label: 'Awaiting Payment', color: 'text-yellow-700 bg-yellow-100', icon: ClockIcon },
  PaymentReceived: { label: 'Payment Received', color: 'text-blue-700 bg-blue-100', icon: ClockIcon },
  InReview: { label: 'Under Review', color: 'text-blue-700 bg-blue-100', icon: ClockIcon },
  Approved: { label: 'Approved', color: 'text-green-700 bg-green-100', icon: CheckCircleIcon },
  Rejected: { label: 'Rejected', color: 'text-red-700 bg-red-100', icon: XCircleIcon },
  Completed: { label: 'Completed', color: 'text-green-700 bg-green-100', icon: CheckCircleIcon },
  Expired: { label: 'Expired', color: 'text-gray-500 bg-gray-100', icon: XCircleIcon },
};

export function DashboardPage() {
  const user = useAuthStore((state) => state.user);

  const {
    data: requests,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['verification-requests'],
    queryFn: () => verificationApi.getRequests().then((res) => res.data),
  });

  // Determine if user can start a new verification
  const terminalStatuses = ['Completed', 'Rejected', 'Expired'];
  const hasActiveRequest = requests?.some(
    (r) => !terminalStatuses.includes(r.status)
  );
  const canStartNew = !hasActiveRequest;

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Welcome back, {user?.firstName || 'User'}
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Manage your marital status verification request
            </p>
          </div>
          {canStartNew && (
            <Link to="/verify" className="btn-primary mt-4 sm:mt-0 inline-flex items-center">
              <PlusIcon className="h-5 w-5 mr-2" />
              New Verification
            </Link>
          )}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : error ? (
          <Alert type="error" message="Failed to load your verification requests" />
        ) : requests?.length === 0 ? (
          <div className="text-center py-12 card">
            <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No verification requests</h3>
            <p className="mt-2 text-sm text-gray-500">
              Get started by creating a new verification request.
            </p>
            <Link to="/verify" className="btn-primary mt-6 inline-flex items-center">
              <PlusIcon className="h-5 w-5 mr-2" />
              Start Verification
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {requests?.map((request) => {
              const status = statusConfig[request.status];
              const StatusIcon = status.icon;

              return (
                <div key={request.id} className="card hover:shadow-md transition-shadow">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-start space-x-4">
                      <div className={`p-2 rounded-lg ${status.color}`}>
                        <StatusIcon className="h-6 w-6" />
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">
                          {request.firstName} {request.lastName}
                        </h3>
                        <p className="text-sm text-gray-500">ID: {request.idNumber}</p>
                        <p className="text-sm text-gray-500">
                          Created: {new Date(request.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 sm:mt-0 flex flex-col sm:items-end space-y-2">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${status.color}`}
                      >
                        {status.label}
                      </span>
                      {request.status === 'Draft' && (
                        <Link
                          to={`/verify?requestId=${request.id}`}
                          className="text-sm text-primary-600 hover:text-primary-500"
                        >
                          Continue Application
                        </Link>
                      )}
                      {request.status === 'PendingPayment' && (
                        <Link
                          to={`/verify?requestId=${request.id}&step=payment`}
                          className="text-sm text-primary-600 hover:text-primary-500"
                        >
                          Complete Payment
                        </Link>
                      )}
                      {request.status === 'Completed' && (
                        <Link
                          to={`/result/${request.id}`}
                          className="text-sm text-primary-600 hover:text-primary-500"
                        >
                          View Result
                        </Link>
                      )}
                      {request.status === 'Rejected' && request.rejectionReason && (
                        <p className="text-sm text-red-600">{request.rejectionReason}</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
